import type { Me, Playback } from "../api/types";
import type { Connection } from "../state/sync";
import { isDedicatedMix } from "./mix";

/**
 * 再生操作ができるか、できないならなぜか。
 * 表示は API の権限（me.can_control・can_dj）に従い、VC の情報は理由の説明にだけ使う。最終判定はサーバー。
 * 理由を一つにまとめず区別する：未接続・更新停止中・Bot停止・同期中・VC不参加・別のVC・権限不足・DJのみ
 */

export type GateReason =
  | "ok"
  | "not_connected"
  | "signed_out"
  | "stale"
  | "loading"
  | "bot_offline"
  | "syncing"
  | "not_in_voice"
  | "other_voice"
  | "no_permission"
  | "dj_required"
  | "mix_operation_unavailable"
  | "playback_owner_required";

export interface Gate {
  ok: boolean;
  reason: GateReason;
  /** ボタンの近くに出す短い説明 */
  text: string;
}

export interface GateContext {
  connected: boolean;
  me: Me | null;
  conn: Connection;
  playback: Playback | null;
}

const ok: Gate = { ok: true, reason: "ok", text: "" };

export function voiceName(name: string | null | undefined, id: string | null | undefined): string {
  if (name) return `#${name}`;
  return id ? "BotのいるVC" : "VC";
}

export type GateLevel = "control" | "dj" | "mix" | "transport" | "enqueue";

export function controlGate(level: GateLevel, ctx: GateContext): Gate {
  if (!ctx.connected) return { ok: false, reason: "not_connected", text: "再生の操作はまだAPIに接続されていません。" };
  if (!ctx.me) return { ok: false, reason: "signed_out", text: "ログインすると操作できます。" };
  if (ctx.conn !== "live") {
    if (ctx.conn === "connecting" || ctx.conn === "idle") return { ok: false, reason: "loading", text: "再生の状態を確認しています…" };
    return { ok: false, reason: "stale", text: "更新停止中のため操作できません。つながり直すのを待っています。" };
  }
  const p = ctx.playback;
  if (!p) return { ok: false, reason: "loading", text: "再生の状態を確認しています…" };
  if (p.syncing) return { ok: false, reason: "syncing", text: "BotがDiscordと状態を合わせています。少し待ってください。" };
  if (!p.worker_available) return { ok: false, reason: "bot_offline", text: "Botが停止しているため操作できません。" };
  if (level === "enqueue" && p.mix?.phase === "active") return {
    ok: false, reason: "mix_operation_unavailable", text: "DJ Mixの再生中は手動で曲を追加できません。Mixを解除すると追加できます。",
  };
  const me = ctx.me;
  if (!me.can_control) {
    if (!me.voice_channel_id) {
      return {
        ok: false,
        reason: "not_in_voice",
        text: p.voice_channel_id
          ? `操作するには ${voiceName(p.voice_channel_name, p.voice_channel_id)} に参加してください。`
          : "操作するには、DiscordのVCに参加してください。",
      };
    }
    if (p.voice_channel_id && me.voice_channel_id !== p.voice_channel_id) {
      return {
        ok: false,
        reason: "other_voice",
        text: `Botは ${voiceName(p.voice_channel_name, p.voice_channel_id)} で再生中です。同じVCに参加すると操作できます。`,
      };
    }
    return { ok: false, reason: "no_permission", text: "再生を操作する権限がありません。" };
  }
  if (level === "dj" && !me.can_dj) return { ok: false, reason: "dj_required", text: "DJ・管理者だけができる操作です。" };
  if ((level === "mix" || level === "transport" && isDedicatedMix(p)) && p.playback_owner_id !== me.id) return {
    ok: false, reason: "playback_owner_required",
    text: p.playback_owner_id ? "DJ Mixの設定・一時停止・再開は、現在の再生オーナーだけが操作できます。"
      : "VCで最初に曲を再生した人がオーナーになり、DJ Mixを設定できます。",
  };
  return ok;
}

/** 本人の未再生の項目は消せる。他人の項目は DJ だけ */
export function canRemoveItem(me: Me | null, ownerId: string): boolean {
  if (!me) return false;
  return me.can_dj || (me.can_control && me.id === ownerId);
}
